import { Menu, Col, Spin, Input } from "antd";
import moment from "moment";
import { useState, useEffect } from "react";
import { LoadingOutlined } from "@ant-design/icons";
import { ReactComponent as QuincyIcon } from "shared/assets/images/quick-quincy-start-icon.svg";
import { isMobile } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { ReactComponent as SearchIcon } from "shared/assets/images/icon-search.svg";
import { ReactComponent as IconClose } from "shared/assets/images/icon-close.svg";
import { ReactComponent as EditIcon } from "shared/assets/images/edit-icon.svg";
import { ReactComponent as SubjectsIcon } from "shared/assets/images/icon-light-bulb.svg";
import { ReactComponent as ClockIcon } from "shared/assets/images/clock-eight.svg";
import { QuincyMenuItem } from "./Quincy";
import {
  ChatContextQuestionEnum,
  i18nEnumLabels,
} from "shared/enums/QuincyChatOriginEnum";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { motion, AnimatePresence } from "framer-motion";

interface ChatHistoryMenuProps {
  menuItems: QuincyMenuItem[];
  chatIdSelected: string | null;
  onClickMenuItem: (info: any) => Promise<void>;
  onClickNewChat: () => Promise<void>;
  loading: boolean;
  hide: boolean;
  onClose: () => void;
  hasChat: boolean;
}

type GroupedItens = {
  today: QuincyMenuItem[];
  yesterday: QuincyMenuItem[];
  lastWeek: QuincyMenuItem[];
  older: QuincyMenuItem[];
  [context: string]: QuincyMenuItem[];
};

const ChatHistoryMenu: React.FC<ChatHistoryMenuProps> = ({
  menuItems,
  chatIdSelected,
  onClickMenuItem,
  loading,
  hide,
  onClose,
  onClickNewChat,
  hasChat,
}) => {
  const { t } = useTranslation();
  const [groupedItems, setGroupedItems] = useState<GroupedItens>({
    today: [],
    yesterday: [],
    lastWeek: [],
    older: [],
  });

  const [search, setSearch] = useState<string>("");
  const [showEmpty, setShowEmpty] = useState(false);

  const [messagesShown, setMessagesShown] = useState<{ [key: string]: number }>(
    {}
  );

  useEffect(() => {
    const grouped: GroupedItens = {
      today: [],
      yesterday: [],
      lastWeek: [],
      older: [],
    };
    menuItems.forEach((item) => {
      const context = item.contextQuestion;
      if (
        context !== undefined &&
        String(context) !== String(ChatContextQuestionEnum.Default)
      ) {
        const contextKey = String(context);
        if (!grouped[contextKey]) grouped[contextKey] = [];
        grouped[contextKey].push(item);
      } else {
        const itemDate = moment(item.createdOn);
        const today = moment();
        const yesterday = moment().subtract(1, "days");
        if (itemDate.isSame(today, "day")) {
          grouped.today.push(item);
        } else if (itemDate.isSame(yesterday, "day")) {
          grouped.yesterday.push(item);
        } else if (itemDate.isAfter(today.subtract(7, "days"))) {
          grouped.lastWeek.push(item);
        } else {
          grouped.older.push(item);
        }
      }
    });
    setGroupedItems(grouped);
  }, [menuItems]);

  useEffect(() => {
    if (!loading && menuItems.length === 0) {
      const timer = setTimeout(() => setShowEmpty(true), 2000);
      return () => clearTimeout(timer);
    } else {
      setShowEmpty(false);
    }
  }, [loading, menuItems.length]);

  const getShownCount = (key: string, total: number) => {
    return messagesShown[key] ?? 3;
  };

  const handleShowMore = (key: string, total: number) => {
    setMessagesShown((prev) => ({ ...prev, [key]: total }));
  };

  const handleShowLess = (key: string) => {
    setMessagesShown((prev) => ({ ...prev, [key]: 3 }));
  };

  const renderMenu = (items: any[], groupKey: string) => {
    const shown = getShownCount(groupKey, items.length);
    const visibleItems = items.slice(0, shown);
    const isExpanded = shown >= items.length;

    const containerVariants = {
      hidden: { opacity: 0 },
      visible: {
        opacity: 1,
        transition: {
          staggerChildren: 0.1,
          delayChildren: 0.2,
        },
      },
    };

    const itemVariants = {
      hidden: {
        opacity: 0,
        y: -20,
        transition: { duration: 0.2 },
      },
      visible: {
        opacity: 1,
        y: 0,
        transition: {
          duration: 0.3,
          type: "spring" as const,
          bounce: 0.2,
        },
      },
    };

    return (
      <>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <AnimatePresence mode="popLayout">
            {visibleItems.map((item, index) => (
              <motion.div key={item.key} variants={itemVariants} layout>
                <Menu
                  mode="inline"
                  theme="dark"
                  items={[item]}
                  className="menu-chats"
                  inlineIndent={0}
                  onClick={onClickMenuItem}
                  selectedKeys={[chatIdSelected ?? ""]}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
        {items.length > 3 && (
          <motion.div
            style={{ margin: "8px 0" }}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            {!isExpanded ? (
              <span
                className="show-more-btn"
                style={{ color: "#CECDFF", cursor: "pointer" }}
                onClick={() => handleShowMore(groupKey, items.length)}
              >
                <FontAwesomeIcon
                  icon={faChevronDown}
                  style={{ fontSize: 10 }}
                />{" "}
                {t("pages.Quincy.showMoreSuggestions") || "Show more"}
              </span>
            ) : (
              <span
                className="show-more-btn"
                style={{ color: "#CECDFF", cursor: "pointer" }}
                onClick={() => handleShowLess(groupKey)}
              >
                <FontAwesomeIcon icon={faChevronUp} style={{ fontSize: 10 }} />{" "}
                {t("pages.Quincy.showLessSuggestions") || "Show less"}
              </span>
            )}
          </motion.div>
        )}
      </>
    );
  };

  const renderLoading = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
        }}
      >
        <Spin
          indicator={<LoadingOutlined style={{ fontSize: 40 }} spin={true} />}
        />
      </div>
    );
  };

  const renderEmptyState = () => {
    return (
      <motion.div
        style={{
          textAlign: "left",
          color: "#cecdff",
          marginTop: "40px",
          width: "100%",
        }}
        initial={{ opacity: 0, y: 20, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <p>{t("pages.Quincy.noChatsYet")}</p>
      </motion.div>
    );
  };

  const renderChats = () => {
    const filter = (items: any[]) => {
      if (!search) return items;
      return items.filter((x: any) =>
        x.label?.toLowerCase().includes(search?.toLowerCase())
      );
    };

    const contextGroups = Object.keys(groupedItems)
      .filter(
        (key) => !["today", "yesterday", "lastWeek", "older"].includes(key)
      )
      .map((contextKey) => {
        const filtered = filter(groupedItems[contextKey]);
        if (!filtered.length) return null;
        const labelKey =
          i18nEnumLabels[
            contextKey as unknown as keyof typeof i18nEnumLabels
          ] || contextKey;
        return (
          <motion.div
            key={contextKey}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          >
            <p className="group-chats">{t(labelKey)}</p>
            {renderMenu(filtered, contextKey)}
          </motion.div>
        );
      });

    const dateGroups = [
      { key: "today", label: t("pages.Quincy.today") },
      { key: "yesterday", label: t("pages.Quincy.yesterday") },
      { key: "lastWeek", label: t("pages.Quincy.lastWeek") },
      { key: "older", label: t("pages.Quincy.olderMessages") },
    ].map(({ key, label }) => {
      const filtered = filter(groupedItems[key]);
      if (!filtered.length) return null;
      return (
        <motion.div
          key={key}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut", delay: 0.1 }}
        >
          <p className="group-chats">{label}</p>
          {renderMenu(filtered, key)}
        </motion.div>
      );
    });

    return (
      <Col xs={24} className="wrapper">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        >
          <h3 style={{ margin: "20px 0" }}>
            <ClockIcon /> {t("pages.Quincy.history")}
          </h3>
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          {dateGroups}
        </motion.div>
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, ease: "easeOut", delay: 0.4 }}
        >
          <h3 style={{ margin: "20px 0" }}>
            <SubjectsIcon /> {t("pages.Quincy.subjects")}
          </h3>
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.6 }}
        >
          {contextGroups}
        </motion.div>
      </Col>
    );
  };

  const isOpen = isMobile ? hide : true;

  const mobileVariants = {
    hidden: {
      x: "-100%",
      opacity: 0,
    },
    visible: {
      x: "0%",
      opacity: 1,
      transition: {
        type: "spring" as const,
        stiffness: 300,
        damping: 30,
        duration: 0.4,
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
    exit: {
      x: "-100%",
      opacity: 0,
      transition: {
        duration: 0.4,
        ease: "easeInOut" as const,
        staggerChildren: 0.05,
        staggerDirection: -1,
        when: "afterChildren",
      },
    },
  };

  const mobileChildVariants = {
    hidden: {
      opacity: 0,
      x: -20,
    },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.3,
        ease: "easeOut" as const,
      },
    },
    exit: {
      opacity: 0,
      x: -30,
      transition: {
        duration: 0.2,
        ease: "easeIn" as const,
      },
    },
  };

  return (
    <AnimatePresence mode="wait" onExitComplete={() => {}}>
      {isOpen && (
        <motion.div
          className={`history-chat ${isMobile && "mobile"}`}
          variants={isMobile ? mobileVariants : undefined}
          initial={isMobile ? "hidden" : undefined}
          animate={isMobile ? "visible" : undefined}
          exit={isMobile ? "exit" : undefined}
        >
          <IconClose
            className={`close-history ${isMobile && "mobile"}`}
            onClick={onClose}
          />
          <div
            className="container-chats scroll-style"
            style={{ display: "flex", flexDirection: "column" }}
          >
            <motion.div variants={isMobile ? mobileChildVariants : undefined}>
              <Col className="header" xs={24}>
                <h1 className="title" style={{ justifyContent: "flex-start" }}>
                  <QuincyIcon width={21} />
                  {t("pages.Quincy.quincy")}
                </h1>
                <h3 className="subtitle">{t("pages.Quincy.title")}</h3>
              </Col>
            </motion.div>
            <motion.div>
              <motion.div variants={isMobile ? mobileChildVariants : undefined}>
                <Col xs={24}>
                  <button
                    className="btn-new-chat mobile"
                    onClick={onClickNewChat}
                  >
                    <EditIcon fill="#1b1a4c" />
                    {t("pages.Quincy.newChat")}
                  </button>
                </Col>
              </motion.div>

              <span className="block" />
              {hasChat && (
                <>
                  <motion.div
                    variants={isMobile ? mobileChildVariants : undefined}
                  >
                    <Col xs={24} className="search-container">
                      <motion.div
                        initial={isMobile ? undefined : { opacity: 0, y: -20 }}
                        animate={isMobile ? undefined : { opacity: 1, y: 0 }}
                        transition={
                          isMobile
                            ? undefined
                            : { duration: 0.4, ease: "easeOut", delay: 0.3 }
                        }
                      >
                        <Input
                          placeholder={t("pages.Quincy.search")}
                          className="search-input"
                          suffix={<SearchIcon />}
                          onChange={(x) => setSearch(x.target.value)}
                        />
                      </motion.div>
                    </Col>
                  </motion.div>
                </>
              )}
            </motion.div>
            <motion.div variants={isMobile ? mobileChildVariants : undefined}>
              {loading && menuItems.length === 0
                ? renderLoading()
                : menuItems.length > 0
                ? renderChats()
                : showEmpty
                ? renderEmptyState()
                : null}
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ChatHistoryMenu;
